import { type FC, Fragment, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';
import { ServerVarsContext } from '../../sharedComponents/ServerVarsContext.tsx';

import { DesignSeriesDescription } from '../DesignSeriesDescription/DesignSeriesDescription.tsx';
import { DesignSeriesHeroByline } from './DesignSeriesHeroByline.tsx';
import { DesignSeriesHeroImages } from './DesignSeriesHeroImages.tsx';

import { type DesignSeriesHero_iconicDesignSeriesPage$data } from './__generated__/DesignSeriesHero_iconicDesignSeriesPage.graphql.ts';

import styles from './DesignSeriesHero.scss';

type Props = {
    iconicDesignSeriesPage: DesignSeriesHero_iconicDesignSeriesPage$data;
};

const DesignSeriesHeroComponent: FC<Props> = props => {
    const { iconicDesignSeriesPage } = props;

    const { isMobile } = useContext(ServerVarsContext);

    const { cmsTopModule } = iconicDesignSeriesPage;
    const { mainImage, bannerImage, title, description } = cmsTopModule || {};
    const imageUrls = [mainImage, bannerImage].filter(Boolean) as Array<string>;

    return (
        <div className={styles.container}>
            <DesignSeriesHeroImages imageUrls={imageUrls} seriesName={title || ''} />
            <div className={styles.bottomSection}>
                <div className={styles.titleContainer}>
                    <div className={styles.title} data-tn="design-series-title">
                        <FormattedMessage
                            defaultMessage="Iconic Designs"
                            id="designSeries.DesignSeriesHero.title"
                        />
                    </div>
                    <h1 className={styles.heading}>
                        <div className={styles.displayName} data-tn="design-series-heading">
                            {title}
                        </div>
                        <DesignSeriesHeroByline iconicDesignSeriesPage={iconicDesignSeriesPage} />
                    </h1>
                </div>
                {description && (
                    <Fragment>
                        {isMobile && (
                            <div className={styles.mobileDescriptionHeading}>
                                <FormattedMessage
                                    defaultMessage="About the Collection"
                                    id="designSeries.DesignSeriesHero.descriptionHeading"
                                />
                            </div>
                        )}
                        <DesignSeriesDescription
                            collapsedHeight={192}
                            descriptionHtml={description}
                            wrapperClass={styles.descriptionContainer}
                        />
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export const DesignSeriesHero = createFragmentContainer(DesignSeriesHeroComponent, {
    iconicDesignSeriesPage: graphql`
        fragment DesignSeriesHero_iconicDesignSeriesPage on IconicDesignSeriesPage {
            cmsTopModule {
                title
                description
                mainImage
                bannerImage
            }
            ...DesignSeriesHeroByline_iconicDesignSeriesPage
        }
    `,
});
